import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import SEOHead from '../common/SEOHead';
import { useInView } from '../../hooks/useInView';
import {
  getPolicyPage,
  FAQ_PAGE,
  PolicyPage as PolicyPageData,
  PolicySection,
  FAQPage as FAQPageData,
  FAQGroup,
  FAQEntry,
} from '../../data/policyContent';
import { getFAQPageSEO, SITE_CONFIG } from '../../utils/seo';
import AuthorByline from '../common/AuthorByline';

// ══════════════════════════════════════════════════════════════════════
// Inline markdown (same as ProductDescription but lighter)
// ══════════════════════════════════════════════════════════════════════
export const inline = (text: string): string =>
  text
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-gold-700 underline hover:text-gold-800 transition-colors">$1</a>')
    .replace(/\*\*(.+?)\*\*/g, '<strong class="font-medium text-forest-900">$1</strong>')
    .replace(/(^|[\s(])_([^_]+?)_(?=[\s.,)!?]|$)/g, '$1<em class="italic text-warm-700">$2</em>');

export const MarkdownBody: React.FC<{ content: string; className?: string }> = ({ content, className = '' }) => {
  const lines = content.split('\n');
  type Block = { type: 'p'; text: string } | { type: 'ul'; items: string[] };
  const blocks: Block[] = [];
  let cur: Block | null = null;
  for (const line of lines) {
    if (line.startsWith('- ')) {
      if (!cur || cur.type !== 'ul') {
        if (cur) blocks.push(cur);
        cur = { type: 'ul', items: [] };
      }
      cur.items.push(line.slice(2).trim());
    } else if (line.trim() === '') {
      if (cur) { blocks.push(cur); cur = null; }
    } else {
      if (!cur || cur.type !== 'p') {
        if (cur) blocks.push(cur);
        cur = { type: 'p', text: '' };
      }
      cur.text += (cur.text ? ' ' : '') + line.trim();
    }
  }
  if (cur) blocks.push(cur);

  return (
    <div className={className}>
      {blocks.map((b, i) =>
        b.type === 'p' ? (
          <p
            key={i}
            className="mb-5 last:mb-0 leading-[1.8] text-[16px] text-warm-700"
            dangerouslySetInnerHTML={{ __html: inline(b.text) }}
          />
        ) : (
          <ul key={i} className="space-y-2.5 mb-5 last:mb-0">
            {b.items.map((item, j) => (
              <li key={j} className="flex items-start gap-3">
                <span className="mt-[0.7em] w-[5px] h-[5px] rounded-full bg-gold-500 flex-shrink-0" />
                <span
                  className="leading-[1.7] text-[16px] text-warm-700"
                  dangerouslySetInnerHTML={{ __html: inline(item) }}
                />
              </li>
            ))}
          </ul>
        )
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Roman numerals for section headers
// ══════════════════════════════════════════════════════════════════════
const roman = (n: number): string => {
  const r: [number, string][] = [[10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
  let out = '';
  for (const [v, s] of r) {
    while (n >= v) { out += s; n -= v; }
  }
  return out;
};

// ══════════════════════════════════════════════════════════════════════
// Reveal wrapper (reused from ProductDescription pattern)
// ══════════════════════════════════════════════════════════════════════
export const Reveal: React.FC<{ children: React.ReactNode; delay?: number; className?: string }> = ({
  children,
  delay = 0,
  className = '',
}) => {
  const { ref, inView } = useInView<HTMLDivElement>({ threshold: 0.1 });
  return (
    <div
      ref={ref}
      className={`transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${
        inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════
// Section divider
// ══════════════════════════════════════════════════════════════════════
export const SectionDivider: React.FC = () => (
  <div className="flex items-center justify-center my-14" aria-hidden="true">
    <div className="h-px bg-warm-200 flex-1 max-w-[110px]" />
    <div className="mx-5 flex items-center gap-2">
      <div className="w-1 h-1 rounded-full bg-gold-400/70" />
      <div className="w-1.5 h-1.5 rounded-full bg-gold-500" />
      <div className="w-1 h-1 rounded-full bg-gold-400/70" />
    </div>
    <div className="h-px bg-warm-200 flex-1 max-w-[110px]" />
  </div>
);

// ══════════════════════════════════════════════════════════════════════
// Page hero header
// ══════════════════════════════════════════════════════════════════════
export const PolicyHero: React.FC<{ eyebrow: string; title: string; intro: string; lastUpdated: string }> = ({
  eyebrow,
  title,
  intro,
  lastUpdated,
}) => (
  <section className="relative bg-cream-50 border-b border-warm-200 overflow-hidden">
    {/* Subtle botanical decoration */}
    <svg
      className="absolute -top-20 -right-20 w-96 h-96 text-forest-100/50 pointer-events-none"
      viewBox="0 0 200 200"
      fill="none"
      stroke="currentColor"
      strokeWidth="0.4"
      aria-hidden="true"
    >
      <circle cx="100" cy="100" r="70" />
      <circle cx="100" cy="100" r="55" />
      <circle cx="100" cy="100" r="40" />
      <path d="M30 100 L170 100 M100 30 L100 170 M50 50 L150 150 M150 50 L50 150" strokeDasharray="2 4" />
    </svg>

    <div className="relative max-w-4xl mx-auto px-6 sm:px-8 py-20 md:py-28">
      <nav className="text-[11px] font-accent italic text-warm-500 mb-10 tracking-wide">
        <Link to="/" className="hover:text-gold-700 transition-colors">Home</Link>
        <span className="mx-2 text-gold-500">•</span>
        <Link to="/policy" className="hover:text-gold-700 transition-colors">Information</Link>
        <span className="mx-2 text-gold-500">•</span>
        <span className="text-forest-900">{title}</span>
      </nav>

      <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-gold-700 font-accent italic mb-4">
        — {eyebrow} —
      </div>
      <h1 className="font-display text-[40px] sm:text-[52px] md:text-[64px] text-forest-900 leading-[1.0] tracking-tight mb-8">
        {title}
      </h1>
      <p className="font-accent text-[18px] md:text-[21px] text-warm-700 leading-[1.7] max-w-[58ch] italic">
        {intro}
      </p>
      <div className="mt-10 flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-warm-400 font-accent italic">
        <div className="w-6 h-px bg-gold-400/60" />
        Last updated {lastUpdated}
        <div className="w-6 h-px bg-gold-400/60" />
      </div>
    </div>
  </section>
);

// ══════════════════════════════════════════════════════════════════════
// Regular content section renderer
// ══════════════════════════════════════════════════════════════════════
export const ContentSection: React.FC<{ section: PolicySection; index: number }> = ({ section, index }) => (
  <section className="relative py-2">
    <div className="flex items-baseline gap-4 mb-5">
      <span
        className="font-accent italic text-xl md:text-2xl text-gold-700 leading-none select-none"
        aria-hidden="true"
      >
        {roman(index)}.
      </span>
      <h2 className="font-display text-[26px] sm:text-[32px] text-forest-900 leading-[1.1] tracking-tight">
        {section.heading}
      </h2>
    </div>
    <div className="pl-0 sm:pl-10">
      <MarkdownBody content={section.body} />
    </div>
  </section>
);

// ══════════════════════════════════════════════════════════════════════
// FAQ group renderer
// ══════════════════════════════════════════════════════════════════════
const FAQGroupSection: React.FC<{ group: FAQGroup; index: number }> = ({ group, index }) => (
  <section className="relative py-2">
    <div className="flex items-baseline gap-4 mb-7">
      <span
        className="font-accent italic text-xl md:text-2xl text-gold-700 leading-none select-none"
        aria-hidden="true"
      >
        {roman(index)}.
      </span>
      <h2 className="font-display text-[26px] sm:text-[32px] text-forest-900 leading-[1.1] tracking-tight">
        {group.title}
      </h2>
    </div>
    <div className="pl-0 sm:pl-10 space-y-6">
      {group.entries.map((entry, i) => (
        <FAQItem key={i} entry={entry} />
      ))}
    </div>
  </section>
);

export const FAQItem: React.FC<{ entry: FAQEntry }> = ({ entry }) => (
  <div className="bg-cream-50 border border-warm-200 rounded-sm overflow-hidden">
    <div className="px-6 py-5 border-b border-warm-200/60 flex items-start gap-4">
      <span
        className="font-accent italic text-gold-700 leading-none flex-shrink-0 select-none text-[20px]"
        aria-hidden="true"
      >
        ❦
      </span>
      <h3 className="font-display text-[18px] sm:text-[19px] text-forest-900 leading-[1.35]">
        {entry.question}
      </h3>
    </div>
    <div className="px-6 py-5 pl-[3.25rem]">
      <p
        className="text-[15px] text-warm-700 leading-[1.7]"
        dangerouslySetInnerHTML={{ __html: inline(entry.answer) }}
      />
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════════
// Main dispatcher — renders either a PolicyPage or the FAQ page
// ══════════════════════════════════════════════════════════════════════
const PolicyPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();

  if (!slug) return <Navigate to="/" replace />;

  // FAQ has a special data shape
  if (slug === 'faq') {
    const data: FAQPageData = FAQ_PAGE;
    return (
      <>
        <SEOHead seoData={getFAQPageSEO(data)} />
        <div className="min-h-screen bg-white">
          <PolicyHero eyebrow={data.eyebrow} title={data.title} intro={data.intro} lastUpdated={data.lastUpdated} />
          <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 md:py-24">
            {data.groups.map((group, idx) => (
              <Reveal key={idx} delay={idx === 0 ? 0 : 60}>
                {idx > 0 && <SectionDivider />}
                <FAQGroupSection group={group} index={idx + 1} />
              </Reveal>
            ))}

            <Reveal delay={120}>
              <div className="mt-20 text-center">
                <div className="flex items-center justify-center gap-3 mb-5" aria-hidden="true">
                  <div className="h-px bg-warm-300 w-10" />
                  <span className="text-gold-500 text-sm">✦</span>
                  <div className="h-px bg-warm-300 w-10" />
                </div>
                <p className="font-accent italic text-[14px] text-warm-500 leading-[1.75] max-w-[52ch] mx-auto">
                  Still have a question? Contact us through the{' '}
                  <Link to="/inquiry" className="text-gold-700 underline hover:text-gold-800">inquiry form</Link>
                  — we read every message.
                </p>
              </div>
            </Reveal>

            <AuthorByline lastUpdated={data.lastUpdated} />
          </div>
        </div>
      </>
    );
  }

  // Regular policy page
  const data: PolicyPageData | null = getPolicyPage(slug);
  if (!data) return <Navigate to="/" replace />;

  return (
    <>
      <SEOHead seoData={{
        title: `${data.title} | LiberaCure`,
        description: data.intro,
        keywords: `${data.title.toLowerCase()}, pharmacy policy, liberacure`,
        openGraph: { title: data.title, description: data.intro, image: SITE_CONFIG.image, url: `${SITE_CONFIG.url}/policy/${data.slug}`, type: 'article' },
        twitter: { card: 'summary', title: data.title, description: data.intro, image: SITE_CONFIG.image },
      }} />
      <div className="min-h-screen bg-white">
        <PolicyHero eyebrow={data.eyebrow} title={data.title} intro={data.intro} lastUpdated={data.lastUpdated} />
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-16 md:py-24">
          {data.sections.map((section, idx) => (
            <Reveal key={idx} delay={idx === 0 ? 0 : 60}>
              {idx > 0 && <SectionDivider />}
              <ContentSection section={section} index={idx + 1} />
            </Reveal>
          ))}

          <Reveal delay={120}>
            <div className="mt-20 pt-12 border-t border-warm-200">
              <div className="flex items-center justify-center gap-3 mb-5" aria-hidden="true">
                <div className="h-px bg-warm-300 w-10" />
                <span className="text-gold-500 text-sm">✦</span>
                <div className="h-px bg-warm-300 w-10" />
              </div>
              <p className="text-center font-accent italic text-[13px] text-warm-500 leading-[1.75] max-w-[52ch] mx-auto">
                Questions about this policy? Reach us through the{' '}
                <Link to="/inquiry" className="text-gold-700 underline hover:text-gold-800">inquiry form</Link>.
              </p>
            </div>
          </Reveal>

          <AuthorByline lastUpdated={data.lastUpdated} />
        </div>
      </div>
    </>
  );
};

export default PolicyPage;
